import { Divider, Text } from '@chakra-ui/react';
import {
  ExternalIngestionDocumentFragment,
  ExternalValidationReportDataFragment,
  GetIngestionDocumentFragment,
  IngestionDocumentReviewFragment,
  IngestionDocumentValidationErrorReviewFragment,
} from '@client/graphql/__generated__/types';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { UngroupedDocumentsSection } from '../components/UngroupedDocumentsSection';
import { groupIngestionDocuments } from '../utils/ingestionDocuments.utils';

interface DefaultDocumentLayoutProps {
  documents: ExternalIngestionDocumentFragment[];
  selectedDocumentId: string | null;
  selectedDocument: GetIngestionDocumentFragment | null;
  validationData: ExternalValidationReportDataFragment | null;
  documentReviews?: IngestionDocumentReviewFragment[];
  submittedDocumentReviews?: IngestionDocumentReviewFragment[];
  hiddenDocumentValidationReviews?: IngestionDocumentValidationErrorReviewFragment[];
  selectFirstDocument?: boolean;
  onDocumentSelect: (
    document: ExternalIngestionDocumentFragment,
    contractDocumentId?: string
  ) => void;
}

export const DefaultDocumentLayout: FC<DefaultDocumentLayoutProps> = ({
  documents,
  selectedDocumentId,
  selectedDocument,
  validationData,
  documentReviews,
  submittedDocumentReviews,
  hiddenDocumentValidationReviews,
  selectFirstDocument = true,
  onDocumentSelect,
}) => {
  const groupedDocuments = useMemo(() => {
    return groupIngestionDocuments(
      documents,
      validationData,
      submittedDocumentReviews
    );
  }, [documents, validationData, submittedDocumentReviews]);

  useEffect(() => {
    if (documents.length > 0 && !selectedDocumentId) {
      const firstDocument =
        groupedDocuments.needsReviewDocuments[0] ||
        groupedDocuments.submittedReviewDocuments[0] ||
        groupedDocuments.otherDocuments[0] ||
        groupedDocuments.archivedDocuments[0] ||
        groupedDocuments.uncheckedDocuments[0] ||
        documents[0];

      if (firstDocument && selectFirstDocument) {
        onDocumentSelect(firstDocument);
      }
    }
  }, [
    documents,
    selectedDocumentId,
    onDocumentSelect,
    groupedDocuments,
    selectFirstDocument,
  ]);

  const handleDocumentSelect = useCallback(
    (
      document: ExternalIngestionDocumentFragment,
      contractDocumentId?: string
    ) => {
      onDocumentSelect(document, contractDocumentId);

      if (contractDocumentId && document.contractDocumentPages) {
        const pageInfo = document.contractDocumentPages[contractDocumentId] as
          | { startPage?: number; endPage?: number }
          | undefined;

        if (pageInfo?.startPage) {
          setTimeout(() => {
            const pageElement = window.document.querySelector(
              `div[data-page-number="${pageInfo.startPage}"]`
            );

            if (pageElement) {
              pageElement.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              });
            }
          }, 100);
        }
      }
    },
    [onDocumentSelect]
  );

  const shouldHideOtherDocuments =
    groupedDocuments.needsReviewDocuments.length > 0 ||
    groupedDocuments.submittedReviewDocuments.length > 0;

  return (
    <>
      {groupedDocuments.needsReviewDocuments.length > 0 && (
        <>
          <UngroupedDocumentsSection
            defaultCollapsed={false}
            documentReviews={documentReviews}
            documents={groupedDocuments.needsReviewDocuments}
            hiddenDocumentValidationReviews={hiddenDocumentValidationReviews}
            selectedDocument={selectedDocument}
            submittedDocumentReviews={submittedDocumentReviews}
            title="Needs Review"
            validationData={validationData}
            onDocumentSelect={handleDocumentSelect}
          />
          <Divider />
        </>
      )}

      {groupedDocuments.submittedReviewDocuments.length > 0 && (
        <>
          <UngroupedDocumentsSection
            defaultCollapsed={false}
            documentReviews={documentReviews}
            documents={groupedDocuments.submittedReviewDocuments}
            hiddenDocumentValidationReviews={hiddenDocumentValidationReviews}
            selectedDocument={selectedDocument}
            submittedDocumentReviews={submittedDocumentReviews}
            title="Submitted Reviews"
            validationData={validationData}
            onDocumentSelect={handleDocumentSelect}
          />
          <Divider />
        </>
      )}

      {groupedDocuments.otherDocuments.length > 0 && (
        <>
          <UngroupedDocumentsSection
            defaultCollapsed={false}
            documentReviews={documentReviews}
            documents={groupedDocuments.otherDocuments}
            hiddenDocumentValidationReviews={hiddenDocumentValidationReviews}
            isCollapsible={shouldHideOtherDocuments}
            selectedDocument={selectedDocument}
            submittedDocumentReviews={submittedDocumentReviews}
            title={shouldHideOtherDocuments ? 'Other Documents' : 'Documents'}
            validationData={validationData}
            onDocumentSelect={handleDocumentSelect}
          />
          <Divider />
        </>
      )}

      {groupedDocuments.archivedDocuments.length > 0 && (
        <>
          <UngroupedDocumentsSection
            isCollapsible
            defaultCollapsed={false}
            documentReviews={documentReviews}
            documents={groupedDocuments.archivedDocuments}
            hiddenDocumentValidationReviews={hiddenDocumentValidationReviews}
            selectedDocument={selectedDocument}
            shouldShowStatus={false}
            submittedDocumentReviews={submittedDocumentReviews}
            title="Archived"
            validationData={validationData}
            onDocumentSelect={handleDocumentSelect}
          />
          <Divider />
        </>
      )}

      {groupedDocuments.uncheckedDocuments.length > 0 && (
        <>
          <UngroupedDocumentsSection
            isCollapsible
            defaultCollapsed={false}
            documentReviews={documentReviews}
            documents={groupedDocuments.uncheckedDocuments}
            hiddenDocumentValidationReviews={hiddenDocumentValidationReviews}
            selectedDocument={selectedDocument}
            shouldShowStatus={false}
            submittedDocumentReviews={submittedDocumentReviews}
            title="Documents Not Checked"
            validationData={validationData}
            onDocumentSelect={handleDocumentSelect}
          />
          <Divider />
        </>
      )}

      {groupedDocuments.blankDocuments.length > 0 && (
        <UngroupedDocumentsSection
          isCollapsible
          defaultCollapsed={false}
          documentReviews={documentReviews}
          documents={groupedDocuments.blankDocuments}
          hiddenDocumentValidationReviews={hiddenDocumentValidationReviews}
          selectedDocument={selectedDocument}
          shouldShowStatus={false}
          submittedDocumentReviews={submittedDocumentReviews}
          title="Blank Documents"
          validationData={validationData}
          onDocumentSelect={handleDocumentSelect}
        />
      )}

      {documents.length === 0 && (
        <Text color="compliance.text.muted">No documents found</Text>
      )}
    </>
  );
};
